/**
 * ProductPage (Карточка товара) — full product detail + one-tap order (ТЗ §3-5, §16-18).
 *
 * Pilot model: each product is directly orderable (no cart, no variants). Shows the hero
 * art (``contain`` — items must not be cropped), title, server-computed price, rating +
 * reviews count, orders count, description / "что входит" / ETA, the primogem cap and
 * star-glitter availability. Two spend fields (star glitter, primogems) drive a debounced
 * server ``quote`` — the total and every limit come from the server, never the client.
 *
 * "Заказать · <total> ₽" creates the order (``POST /api/orders/``) and lands the user in
 * the order chat. A reviews section lists the product's visible reviews.
 */
import { useRef, useState } from "react";

import { Price, Stars } from "@melusine/shared-ui";
import { useProduct } from "@entities/product";
import { useProductReviews, type Review } from "@entities/review";
import { useMe } from "@entities/user";
import {
  attemptKey,
  parseSpend,
  useCreateOrder,
  useProductQuote,
} from "@features/product-order";
import { StatePanel } from "@widgets/catalog-browser";
import { ApiError } from "@shared/api";
import { routes, useNavigate } from "@shared/router";

import styles from "./ProductPage.module.css";

export interface ProductPageProps {
  readonly productId: number;
}

const rubFormatter = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "RUB",
  maximumFractionDigits: 0,
});

/** Format a server-provided ruble amount the same way as the {@link Price} face. */
function formatRub(value: string | number): string {
  const numeric = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(numeric) ? rubFormatter.format(numeric) : String(value);
}

/** Split a free-text "что входит" field (newline- or bullet-separated) into items. */
function includedItems(raw: string): readonly string[] {
  return raw
    .split(/\r?\n/)
    .map((line) => line.replace(/^[\s•·*-]+/, "").trim())
    .filter((line) => line.length > 0);
}

function apiErrorText(err: ApiError, fallback: string): string {
  const payload = err.payload as { readonly detail?: unknown } | null;
  if (payload !== null && typeof payload.detail === "string") {
    return payload.detail;
  }
  return fallback;
}

export function ProductPage({ productId }: ProductPageProps): JSX.Element {
  const navigate = useNavigate();
  const { data: product, isLoading, error, refetch } = useProduct(productId);
  const { data: me } = useMe();

  const [glitterInput, setGlitterInput] = useState("");
  const [primogemInput, setPrimogemInput] = useState("");

  const glitterSpend = parseSpend(glitterInput);
  const primogemSpend = parseSpend(primogemInput);

  const quoteState = useProductQuote(productId, glitterSpend, primogemSpend);
  const signature = attemptKey(productId, glitterSpend, primogemSpend);

  const orderInput = product
    ? {
        product_id: product.id,
        star_glitter_spend: glitterSpend,
        primogem_spend: primogemSpend,
      }
    : null;
  const { submit, isSubmitting, error: createError } = useCreateOrder(
    orderInput,
    signature,
  );
  const submittedRef = useRef(false);

  if (isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (error || !product) {
    return (
      <StatePanel
        kind="error"
        message="Товар не найден или недоступен."
        onRetry={refetch}
      />
    );
  }

  const { quote } = quoteState;
  const total = quote?.total_rub ?? product.base_price;
  const quoteHasError = quoteState.error !== null;
  const canOrder = !quoteHasError && !quoteState.isLoading && !isSubmitting;

  const included = product.whats_included
    ? includedItems(product.whats_included)
    : [];
  const ratingValue = Number(product.rating_avg);
  const balance = me?.balance;

  const goBack = (): void => {
    navigate(routes.shopGame(product.game));
  };

  const placeOrder = (): void => {
    if (submittedRef.current || !canOrder) {
      return;
    }
    submittedRef.current = true;
    submit()
      .then((order) => {
        navigate(routes.order(order.id));
      })
      .catch(() => {
        // Allow a retry — the stable idempotency key keeps it safe server-side.
        submittedRef.current = false;
      });
  };

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <button
          type="button"
          className={styles.iconButton}
          aria-label="Назад"
          onClick={goBack}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
        </button>
      </header>

      <div className={styles.scroll}>
        <div className={styles.media}>
          {product.image_url ? (
            <img src={product.image_url} alt="" className={styles.image} />
          ) : (
            <span className={styles.placeholder} aria-hidden="true">
              ✦
            </span>
          )}
        </div>

        <div className={styles.heading}>
          <h1 className={styles.title}>{product.title}</h1>
          <div className={styles.metaRow}>
            {product.reviews_count > 0 ? (
              <span className={styles.rating}>
                <Stars value={ratingValue} size="sm" showValue />
                <span className={styles.reviewsCount}>
                  · {product.reviews_count}{" "}
                  {reviewsWord(product.reviews_count)}
                </span>
              </span>
            ) : (
              <span className={styles.noRating}>Нет отзывов</span>
            )}
            {product.orders_count > 0 ? (
              <span className={styles.ordersCount}>
                {product.orders_count} {ordersWord(product.orders_count)}
              </span>
            ) : null}
          </div>
        </div>

        <div className={styles.priceRow}>
          <Price amount={product.base_price} accent size="lg" />
        </div>

        {product.description ? (
          <p className={styles.description}>{product.description}</p>
        ) : null}

        {included.length > 0 ? (
          <section className={styles.includedCard} aria-label="Что входит">
            <h2 className={styles.includedTitle}>Что входит</h2>
            {included.map((item, index) => (
              <div className={styles.includedRow} key={index}>
                <svg
                  className={styles.checkIcon}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--ms-success)"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m5 13 4 4L19 7" />
                </svg>
                <span className={styles.includedText}>{item}</span>
              </div>
            ))}
          </section>
        ) : null}

        <div className={styles.infoList}>
          {product.eta_text ? (
            <div className={styles.infoRow}>
              <svg
                className={styles.infoIcon}
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--ms-text-muted)"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              <span className={styles.infoLabel}>Срок выполнения</span>
              <span className={styles.infoValue}>{product.eta_text}</span>
            </div>
          ) : null}

          {product.primogem_cap !== null ? (
            <div className={styles.infoRow} data-tone="primogem">
              <span className={styles.primogemMark} aria-hidden="true" />
              <span className={styles.infoLabel}>Списать примогемами</span>
              <span className={styles.infoValuePrimogem}>
                до {formatRub(product.primogem_cap)}
              </span>
            </div>
          ) : null}

          <div className={styles.infoRow} data-tone="glitter">
            <svg
              className={styles.glitterIcon}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="var(--ms-glitter)"
            >
              <path d="M12 2.8c.55 4.7 1.7 5.85 6.4 6.4-4.7.55-5.85 1.7-6.4 6.4-.55-4.7-1.7-5.85-6.4-6.4 4.7-.55 5.85-1.7 6.4-6.4z" />
            </svg>
            <span className={styles.infoLabel}>Оплата звёздным блеском</span>
            <span className={styles.infoValueGlitter}>
              {product.star_glitter_allowed ? "доступна" : "—"}
            </span>
          </div>
        </div>

        {/* ── currency spend (server-priced) ─────────────────────────── */}
        <section className={styles.spendCard} aria-label="Списание валют">
          <h2 className={styles.spendTitle}>Списать валюту</h2>
          {product.star_glitter_allowed ? (
            <SpendField
              label="Звёздный блеск"
              glyph="✦"
              value={glitterInput}
              balance={balance?.star_glitter}
              discountRub={quote?.star_glitter_discount_rub}
              max={balance?.star_glitter}
              onChange={setGlitterInput}
            />
          ) : null}
          <SpendField
            label="Примогемы"
            glyph="◆"
            value={primogemInput}
            balance={balance?.primogems}
            discountRub={quote?.primogem_discount_rub}
            max={balance?.primogems}
            onChange={setPrimogemInput}
          />
          <div className={styles.spendTotals}>
            <div className={styles.spendRow}>
              <span className={styles.spendRowLabel}>Сумма</span>
              <Price amount={quote?.subtotal ?? product.base_price} size="sm" />
            </div>
            <div className={styles.spendGrand}>
              <span className={styles.spendGrandLabel}>К оплате</span>
              <Price amount={total} accent size="lg" />
            </div>
          </div>
          {quoteHasError ? (
            <p className={styles.spendError} role="alert">
              {apiErrorText(
                quoteState.error as ApiError,
                "Превышен лимит списания. Уменьшите сумму.",
              )}
            </p>
          ) : null}
        </section>

        <ReviewsSection productId={productId} count={product.reviews_count} />
      </div>

      <div className={styles.bottomBar}>
        {createError !== null && !quoteHasError ? (
          <p className={styles.orderError} role="alert">
            {apiErrorText(createError, "Не удалось оформить заказ.")}
          </p>
        ) : null}
        <button
          type="button"
          className={styles.orderAction}
          disabled={!canOrder}
          onClick={placeOrder}
        >
          {isSubmitting ? "Оформляем…" : `Заказать · ${formatRub(total)}`}
        </button>
      </div>
    </div>
  );
}

interface SpendFieldProps {
  readonly label: string;
  readonly glyph: string;
  readonly value: string;
  readonly balance: number | undefined;
  readonly discountRub: string | undefined;
  readonly max: number | undefined;
  readonly onChange: (next: string) => void;
}

function SpendField({
  label,
  glyph,
  value,
  balance,
  discountRub,
  max,
  onChange,
}: SpendFieldProps): JSX.Element {
  const discount = discountRub !== undefined ? Number(discountRub) : 0;
  return (
    <label className={styles.spendField}>
      <span className={styles.spendGlyph} aria-hidden="true">
        {glyph}
      </span>
      <span className={styles.spendInfo}>
        <span className={styles.spendLabel}>{label}</span>
        {balance !== undefined ? (
          <span className={styles.spendBalance}>
            Доступно: {balance.toLocaleString("ru-RU")}
          </span>
        ) : null}
      </span>
      <span className={styles.spendInputWrap}>
        <input
          className={styles.spendInput}
          type="number"
          inputMode="numeric"
          min={0}
          {...(max !== undefined ? { max } : {})}
          placeholder="0"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        {discount > 0 ? (
          <span className={styles.spendDiscount}>−{discount.toFixed(0)} ₽</span>
        ) : null}
      </span>
    </label>
  );
}

interface ReviewsSectionProps {
  readonly productId: number;
  readonly count: number;
}

function ReviewsSection({
  productId,
  count,
}: ReviewsSectionProps): JSX.Element | null {
  const { data: reviews, isLoading } = useProductReviews(productId);

  if (count === 0 && (!reviews || reviews.length === 0)) {
    return null;
  }

  return (
    <section className={styles.reviews} aria-label="Отзывы">
      <h2 className={styles.reviewsTitle}>Отзывы</h2>
      {isLoading ? (
        <p className={styles.reviewsMuted}>Загрузка…</p>
      ) : !reviews || reviews.length === 0 ? (
        <p className={styles.reviewsMuted}>Отзывов пока нет.</p>
      ) : (
        <div className={styles.reviewList}>
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      )}
    </section>
  );
}

const reviewDateFormatter = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** Format an ISO timestamp as "12 марта 2026, 14:05"; falls back to the raw value. */
function formatReviewDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : reviewDateFormatter.format(date);
}

/** First two initials for the avatar fallback when no image is available. */
function authorInitials(name: string): string {
  const cleaned = name.replace(/^@/, "").trim();
  return cleaned ? cleaned.slice(0, 2).toUpperCase() : "✦";
}

function ReviewCard({ review }: { readonly review: Review }): JSX.Element {
  const [avatarFailed, setAvatarFailed] = useState(false);
  const showAvatar = review.author_avatar_url.length > 0 && !avatarFailed;

  return (
    <article className={styles.reviewCard}>
      <header className={styles.reviewHead}>
        <span className={styles.reviewAvatar} aria-hidden="true">
          {showAvatar ? (
            <img
              src={review.author_avatar_url}
              alt=""
              className={styles.reviewAvatarImg}
              referrerPolicy="no-referrer"
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <span className={styles.reviewAvatarLabel}>
              {authorInitials(review.author_name)}
            </span>
          )}
        </span>
        <span className={styles.reviewMeta}>
          <span className={styles.reviewAuthor}>{review.author_name}</span>
          <span className={styles.reviewDate}>
            {formatReviewDate(review.created_at)}
          </span>
        </span>
        <Stars value={review.rating} size="sm" showValue />
      </header>
      {review.text ? (
        <p className={styles.reviewText}>{review.text}</p>
      ) : null}
      {review.shop_reply ? (
        <p className={styles.reviewReply}>
          <span className={styles.reviewReplyLabel}>Ответ магазина:</span>{" "}
          {review.shop_reply}
        </p>
      ) : null}
    </article>
  );
}

function reviewsWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return "отзыв";
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return "отзыва";
  }
  return "отзывов";
}

function ordersWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return "заказ";
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return "заказа";
  }
  return "заказов";
}
